import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import APSLogo from "../../../components/ui/APS Logo.svg";
import {
  LayoutGrid,
  Users,
  Building2,
  CreditCard,
  Wallet,
  Star,
  User,
  LogOut,
  Shield,
  X,
  ChevronDown,
  FileBarChart2,
  FileText,
  MessageSquare,
  CalendarClock,
} from "lucide-react";
import { useAuth } from "../../auth/hooks/useAuth";
import { REPORT_OPTIONS } from "../constants/reportOptions";

const DASHBOARD_LINKS = [
  { label: "Overview", to: "/admin", icon: LayoutGrid },
];

const MANAGEMENT_LINKS = [
  { label: "User Management", to: "/admin/users", icon: Users },
  { label: "Enterprise Contacts", to: "/admin/enterprise-contacts", icon: Building2 },
  { label: "Payment History", to: "/admin/payment-history", icon: Wallet },
  { label: "Testimonials", to: "/admin/testimonials", icon: Star },
  { label: "Pricing Management", to: "/admin/pricing", icon: CreditCard },
  { label: "Feedback",    to: "/admin/feedback",  icon: MessageSquare },
  { label: "Policies",    to: "/admin/policies",  icon: FileText },
  { label: "Cache Days Setup", to: "/admin/analysis-cooldown", icon: CalendarClock },
  { label: "Profile",     to: "/admin/profile",   icon: User },
];

const navItemClassName = ({ isActive }) =>
  `group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
    isActive
      ? "bg-[#5B4FCF]/10 text-[#5B4FCF] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-full before:bg-[#5B4FCF]"
      : "text-brand-muted hover:bg-[#5B4FCF]/5 hover:text-brand-text"
  }`;

const AdminSidebar = ({ onClose }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const isReportsRoute = location.pathname === "/admin/reports";
  const selectedReportView = new URLSearchParams(location.search).get("view") || REPORT_OPTIONS[0].key;
  const [reportsOpen, setReportsOpen] = useState(isReportsRoute);

  useEffect(() => {
    if (isReportsRoute) setReportsOpen(true);
  }, [isReportsRoute]);

  const handleLogout = async () => {
    await logout();
    window.location.href = "/auth/login";
  };

  return (
    <aside className="w-64 h-full bg-white border-r border-surface-border flex flex-col">
      {/* Logo */}
      <div className="flex items-center justify-between gap-2.5 px-5 h-16 border-b border-surface-border">
        <div className="flex items-center gap-2.5">
          <img src={APSLogo} alt="APS Logo" className="h-12 w-auto object-contain" />
        </div>
        <button
          onClick={onClose}
          className="lg:hidden text-brand-muted hover:text-brand-text p-1 rounded-lg hover:bg-surface transition-colors"
          aria-label="Close sidebar"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-0.5 px-3 pt-4 flex-1 overflow-y-auto">
        {DASHBOARD_LINKS.map(({ label, to, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/admin"}
            onClick={onClose}
            className={navItemClassName}
          >
            {({ isActive }) => (
              <>
                <Icon className={`w-4 h-4 flex-shrink-0 transition-colors ${isActive ? "text-[#5B4FCF]" : "text-brand-muted group-hover:text-brand-text"}`} />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}

        {MANAGEMENT_LINKS.map(({ label, to, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/admin"}
            onClick={onClose}
            className={navItemClassName}
          >
            {({ isActive }) => (
              <>
                <Icon className={`w-4 h-4 flex-shrink-0 transition-colors ${isActive ? "text-[#5B4FCF]" : "text-brand-muted group-hover:text-brand-text"}`} />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}

        <button
          type="button"
          onClick={() => setReportsOpen((value) => !value)}
          className={`w-full relative flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
            isReportsRoute
              ? "bg-[#5B4FCF]/10 text-[#5B4FCF] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-full before:bg-[#5B4FCF]"
              : "text-brand-muted hover:bg-[#5B4FCF]/5 hover:text-brand-text"
          }`}
        >
          <span className="flex items-center gap-3">
            <FileBarChart2 className={`w-4 h-4 flex-shrink-0 transition-colors ${isReportsRoute ? "text-[#5B4FCF]" : "text-brand-muted"}`} />
            Reports
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${reportsOpen ? "rotate-180" : ""}`} />
        </button>

        {reportsOpen && (
          <div className="ml-3 pl-2 border-l border-surface-border flex flex-col gap-0.5">
            {REPORT_OPTIONS.map((item) => {
              const isActive = isReportsRoute && selectedReportView === item.key;
              return (
                <Link
                  key={item.key}
                  to={`/admin/reports?view=${item.key}`}
                  onClick={onClose}
                  className={`block px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                    isActive
                      ? "text-[#5B4FCF] bg-[#5B4FCF]/10"
                      : "text-brand-muted hover:text-brand-text hover:bg-[#5B4FCF]/5"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      {/* Logout */}
      <div className="px-3 pb-4 border-t border-surface-border pt-3">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-brand-muted hover:bg-[#5B4FCF]/5 hover:text-brand-text transition-all duration-200 w-full"
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          Logout
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
